import type { TFunction } from 'i18next';

import {
  CloudflareIcon,
  FacebookIcon,
  GoogleFlatIcon,
  StripeIcon,
} from '@/components/_icons';

import type { CookieCategory } from './types';

export const getCookieConsentCategories = (
  t: TFunction<'common', 'CookieConsentDetails'>,
): CookieCategory[] => [
  {
    id: 'necessary',
    title: t('categories.necessary.title'),
    required: true,
    enabled: true,
    description: t('categories.necessary.description'),
    trackers: [
      {
        id: 'stripe',
        name: 'Stripe',
        description: t('trackers.stripe.description'),
        Icon: StripeIcon,
        items: [
          {
            name: '__stripe_sid',
            description: t('trackers.stripe.__stripe_sid.description'),
            expiry: t('minute', { count: 30 }),
            type: 'HTTP',
          },
          {
            name: '__stripe_mid',
            description: t('trackers.stripe.__stripe_mid.description'),
            expiry: t('year', { count: 1 }),
            type: 'HTTP',
          },
          {
            name: 'm',
            description: t('trackers.stripe.m.description'),
            expiry: t('year', { count: 2 }),
            type: 'HTTP',
          },
          {
            name: 'cookie-perms',
            description: t('trackers.stripe.cookie-perms.description'),
            expiry: t('month', { count: 6 }),
            type: 'HTTP',
          },
        ],
      },
      {
        id: 'google',
        name: 'Google',
        description: t('trackers.google.description'),
        Icon: GoogleFlatIcon,
        items: [
          {
            name: 'G_ENABLED_IDPS',
            description: t('trackers.google.G_ENABLED_IDPS.description'),
            expiry: '∞',
            type: 'HTTP',
          },
          {
            name: 'NID',
            description: t('trackers.google.NID.description'),
            expiry: t('month', { count: 6 }),
            type: 'HTTP',
          },
          {
            name: '1P_JAR',
            description: t('trackers.google.1P_JAR.description'),
            expiry: t('month', { count: 1 }),
            type: 'HTTP',
          },
          {
            name: 'SID',
            description: t('trackers.google.SID.description'),
            expiry: t('year', { count: 2 }),
            type: 'HTTP',
          },
          {
            name: 'SSID',
            description: t('trackers.google.SSID.description'),
            expiry: t('year', { count: 2 }),
            type: 'HTTP',
          },
          {
            name: 'HSID',
            description: t('trackers.google.HSID.description'),
            expiry: t('year', { count: 2 }),
            type: 'HTTP',
          },
          {
            name: 'SIDCC',
            description: t('trackers.google.SIDCC.description'),
            expiry: t('year', { count: 1 }),
            type: 'HTTP',
          },
          {
            name: 'APISID',
            description: t('trackers.google.APISID.description'),
            expiry: t('year', { count: 2 }),
            type: 'HTTP',
          },
          {
            name: '__Secure-1PSID',
            description: t('trackers.google.__Secure-1PSID.description'),
            expiry: t('year', { count: 2 }),
            type: 'HTTP',
          },
          {
            name: '__Secure-1PAPISID',
            description: t('trackers.google.__Secure-1PAPISID.description'),
            expiry: t('year', { count: 2 }),
            type: 'HTTP',
          },
          {
            name: '__Secure-3PAPISID',
            description: t('trackers.google.__Secure-3PAPISID.description'),
            expiry: t('year', { count: 2 }),
            type: 'HTTP',
          },
          {
            name: '__Secure-3PSID',
            description: t('trackers.google.__Secure-3PSID.description'),
            expiry: t('year', { count: 2 }),
            type: 'HTTP',
          },
          {
            name: '__Secure-3PSIDCC',
            description: t('trackers.google.__Secure-3PSIDCC.description'),
            expiry: t('year', { count: 1 }),
            type: 'HTTP',
          },
          {
            name: '__Secure-APISID',
            description: t('trackers.google.__Secure-APISID.description'),
            expiry: t('year', { count: 2 }),
            type: 'HTTP',
          },
          {
            name: '__Secure-HSID',
            description: t('trackers.google.__Secure-HSID.description'),
            expiry: t('year', { count: 2 }),
            type: 'HTTP',
          },
          {
            name: '__Secure-SSID',
            description: t('trackers.google.__Secure-SSID.description'),
            expiry: t('year', { count: 2 }),
            type: 'HTTP',
          },
        ],
      },
      {
        id: 'cloudflare',
        name: 'Cloudflare',
        description: t('trackers.cloudflare.description'),
        Icon: CloudflareIcon,
        items: [
          {
            name: '__cf_bm',
            description: t('trackers.cloudflare.__cf_bm.description'),
            expiry: t('minute', { count: 30 }),
            type: 'HTTP',
          },
        ],
      },
    ],
  },
  {
    id: 'statistics',
    title: t('categories.statistical.title'),
    required: false,
    enabled: false,
    description: t('categories.statistical.description'),
    trackers: [
      {
        id: 'google-analytics',
        name: 'Google Analytics',
        description: t('trackers.google-analytics.description'),
        Icon: GoogleFlatIcon,
        items: [
          {
            name: 'DV',
            description: t('trackers.google-analytics.DV.description'),
            expiry: t('hour', { count: 6 }),
            type: 'HTTP',
          },
          {
            name: 'OTZ',
            description: t('trackers.google-analytics.OTZ.description'),
            expiry: t('year', { count: 2 }),
            type: 'HTTP',
          },
          {
            name: '_gat',
            description: t('trackers.google-analytics._gat.description'),
            expiry: t('minute', { count: 1 }),
            type: 'HTTP',
          },
          {
            name: '_gid',
            description: t('trackers.google-analytics._gid.description'),
            expiry: t('hour', { count: 24 }),
            type: 'HTTP',
          },
        ],
      },
      {
        id: 'facebook-pixel',
        name: 'Facebook Pixel',
        description: t('trackers.facebook-pixel.description'),
        Icon: FacebookIcon,
        items: [
          {
            name: '_fbp',
            description: t('trackers.facebook-pixel._fbp.description'),
            expiry: t('month', { count: 15 }),
            type: 'HTTP',
          },
          {
            name: 'fr',
            description: t('trackers.facebook-pixel.fr.description'),
            expiry: t('month', { count: 3 }),
            type: 'HTTP',
          },
          {
            name: 'datr',
            description: t('trackers.facebook-pixel.datr.description'),
            expiry: t('year', { count: 2 }),
            type: 'HTTP',
          },
        ],
      },
    ],
  },
];
